/**
 * Auth bootstrap hook.
 *
 * Bootstraps authenticated session from backend cookie on app startup.
 * This ensures the frontend auth state is synchronized with the backend
 * session cookie after page reload or navigation.
 *
 * Security posture:
 * - Session is stored in httpOnly cookie (backend)
 * - Frontend calls /me endpoint to validate session and get sponsor access state
 * - No sensitive data persisted in localStorage
 * - Auth state (including sponsor access) is derived from server validation
 *
 * Lifecycle:
 * - In production, ALWAYS re-validates session on app startup (no stale cache).
 * - In E2E mode (VITE_E2E_AUTH_INJECTION=true), the server bootstrap is skipped
 *   ONLY when window-state injection already authenticated the store. This is
 *   the exact contract required by the E2E auth-injection seam (main.tsx
 *   calls initializeE2EAuthInjection() before React mounts).
 *   Production mounting must continue to call getSession().
 * - Uses authStatus to indicate loading/error states.
 * - On transient errors, preserves current state for retry.
 */
import React, { useEffect, useRef } from 'react';
import { getSession } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import {
  isE2EAuthInjectionEnabled,
  isE2EWindowStateInjected,
} from '@/store/e2eAuthInjection';

/**
 * Hook to bootstrap auth state from backend session cookie.
 *
 * Production behavior: always validates with server on mount.
 * E2E behavior: skips getSession() when window-state injection has already
 * authenticated the store. The skip is E2E-scoped so production mounts
 * with an authenticated in-memory state still revalidate with the server
 * (per the server-authoritative bootstrap contract).
 */
export function useAuthBootstrap() {
  const bootstrapStart = useAuthStore((state) => state.bootstrapStart);
  const hydrateFromSession = useAuthStore((state) => state.hydrateFromSession);
  const bootstrapFailure = useAuthStore((state) => state.bootstrapFailure);

  // Use ref to track if bootstrap has run in this runtime.
  const bootstrapRan = useRef(false);

  useEffect(() => {
    // Prevent duplicate bootstrap calls.
    if (bootstrapRan.current) {
      return;
    }
    bootstrapRan.current = true;

    // E2E-scoped skip: only when E2E injection is enabled AND the
    // window state was actually injected for this mount. Production
    // mounts with 'unknown' status, or E2E mounts where the window
    // state was NOT injected (e.g. b2b-join profile-media, which
    // uses a real session cookie), will proceed to getSession().
    //
    // Without the window-state check, the b2b-join suite (E2E mode
    // but no window-state injection) would skip the bootstrap on
    // page.reload() because authStatus='authenticated' is persisted
    // to localStorage but accountId is not. The gate in
    // ProfileQuestionnairePage (CORRECTION03 C1) requires both,
    // so without re-validation the page would be stuck in the
    // loading shell after every reload.
    const currentStatus = useAuthStore.getState().authStatus;
    if (
      isE2EAuthInjectionEnabled() &&
      isE2EWindowStateInjected() &&
      currentStatus === 'authenticated'
    ) {
      return;
    }

    const bootstrap = async () => {
      // Set loading state
      bootstrapStart();

      try {
        const session = await getSession();

        if (session) {
          // Session found - hydrate full auth state including sponsor access from backend
          hydrateFromSession(session);
        } else {
          // No session - user is anonymous
          useAuthStore.setState({
            authStatus: 'anonymous',
            sessionId: null,
            isAuthenticated: false,
            sponsorAccessState: 'unknown',
            hasSponsorAccess: false,
            sponsorTier: 'none',
            isAdmin: false,
          });
        }
      } catch (error) {
        // Session check failed - set unknown status for retry
        // Preserve current session state (don't clear on transient errors)
        console.warn('Auth bootstrap failed:', error);
        bootstrapFailure();
      }
    };

    bootstrap();
  }, [bootstrapStart, hydrateFromSession, bootstrapFailure]);
}

/**
 * Hook to get current auth status.
 * Returns the explicit authStatus from store.
 */
export function useAuthStatus() {
  return useAuthStore((state) => state.authStatus);
}

/**
 * Bootstrap provider component for auth initialization.
 * Wraps the app and ensures auth state is loaded from backend on startup.
 */
export function AuthBootstrapProvider({ children }: { children: React.ReactNode }) {
  useAuthBootstrap();
  return <>{children}</>;
}
